import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, type PromotionLabel } from './api';

/**
 * Stitky akci pro dlazdice a detail produktu.
 *
 * Dlazdice jsou roztrousene po cele vyloze (katalog, pasy, oblibene, souvisejici),
 * takze kazda by jinak strilela vlastni dotaz. Provider misto toho posbira
 * vyzadana `productId` z aktualniho vykresleni, jednim POST je dotahne po
 * davkach (server bere max 60 na dotaz) a vysledek drzi v cache.
 *
 * FAIL-SILENT: kdyz endpoint selze nebo je pomaly, stitky se proste nevykresli.
 * Nikdy nesmi zdrzet ani shodit vypis produktu (vzor z Faze 7). I id, ktera
 * server nevrati, se do cache ulozi jako prazdne pole, aby se nedotahovala
 * dokola.
 */
interface LabelState {
  /** Zaregistruje produkt k dotazeni (pokud uz neni v cache). */
  request(productId: string): void;
  /** Stitky produktu; `undefined` dokud nedorazi (nebo pri fail-silent). */
  labels(productId: string): readonly PromotionLabel[] | undefined;
}

const LabelContext = createContext<LabelState | null>(null);

/** Kolik ms se ceka na posbirani id z jednoho vykresleni, nez se odesle davka. */
const FLUSH_DELAY_MS = 60;
/** Server bere max 60 id na dotaz. */
const MAX_BATCH = 60;

export function PromotionLabelsProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState<Record<string, readonly PromotionLabel[]>>({});
  // Id, ktera uz jsou v cache nebo se prave dotahuji – proti opakovanemu dotazu.
  const known = useRef<Set<string>>(new Set());
  const pending = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current !== undefined) clearTimeout(timer.current);
    };
  }, []);

  const flush = useCallback(() => {
    timer.current = undefined;
    const batch = [...pending.current].slice(0, MAX_BATCH);
    if (batch.length === 0) return;
    for (const id of batch) pending.current.delete(id);

    void api
      .fetchPromotionLabels(batch)
      .then((result) => {
        if (!alive.current) return;
        setCache((current) => {
          const next = { ...current };
          // I id bez akce ulozime jako prazdne pole – uz se nedotahuje znovu.
          for (const id of batch) next[id] = result.labels[id] ?? [];
          return next;
        });
      })
      .catch(() => {
        // Fail-silent: povazujeme id za vyrizena (prazdna), at katalog nezasekne
        // opakovanym dotazovanim. Stitky se u teto davky proste neukazou.
        if (!alive.current) return;
        setCache((current) => {
          const next = { ...current };
          for (const id of batch) if (next[id] === undefined) next[id] = [];
          return next;
        });
      });

    // Kdyby se seslo vic nez MAX_BATCH id, dober zbytek dalsim kolem.
    if (pending.current.size > 0 && timer.current === undefined) {
      timer.current = setTimeout(flush, FLUSH_DELAY_MS);
    }
  }, []);

  const requestLabel = useCallback(
    (productId: string) => {
      if (productId === '' || known.current.has(productId)) return;
      known.current.add(productId);
      pending.current.add(productId);
      if (timer.current === undefined) timer.current = setTimeout(flush, FLUSH_DELAY_MS);
    },
    [flush],
  );

  const value = useMemo<LabelState>(
    () => ({
      request: requestLabel,
      labels: (productId: string) => cache[productId],
    }),
    [requestLabel, cache],
  );

  return <LabelContext.Provider value={value}>{children}</LabelContext.Provider>;
}

/**
 * Stitky akci pro jeden produkt. Prvni render produkt zaregistruje k dotazeni;
 * jakmile davka dorazi, hook vrati stitky. Bez provideru (napr. v testu) vraci
 * prazdne pole – nikdy nespadne.
 */
export function usePromotionLabels(productId: string): readonly PromotionLabel[] {
  const context = useContext(LabelContext);
  useEffect(() => {
    if (context !== null) context.request(productId);
  }, [context, productId]);
  return context?.labels(productId) ?? [];
}
